import { Fragment, useEffect, useState, useMemo } from "react";
import { useSnackbar,useTranslation } from "@1f/react-sdk";
import { LogApi } from "../../businessLogic/api/logApi";
import SignatureApi from "../../businessLogic/api/signatureApi";
import sharedDatasInstance from "../../businessLogic/sharedDatas";
import { bsaFascicolo } from "../../businessLogic/constants/securityAreas";
import { useAreaPermissions } from "../../businessLogic/hooks/useAreaPermissions";
import VaporUploader from "@vapor/react-custom/VaporUploader";
import Stack from "@vapor/react-material/Stack";
import Typography from "@vapor/react-material/Typography";
import IconButton from "@vapor/react-material/IconButton";
import Button from "@vapor/react-material/Button";
import VaporTag from "@vapor/react-custom/VaporTag";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Loading } from "../Loading";
import { NotAuth } from "../NotAuth";
import { DataGridPro, GridToolbarContainer, GridToolbarFilterButton, GridToolbarColumnsButton, itIT } from "@mui/x-data-grid-pro";
import {
    Autorenew as RefreshIcon,
    DrawOutlined as SignatureIcon,
    ArrowForward as DetailIcon,
    DeleteOutlineOutlined as DeleteIcon,
    ArchiveOutlined as ArchiveIcon,
} from "@mui/icons-material";   
import { ModalSignature } from "../ModalSignature";
import { ModalConservazione } from "../ModalConservazione";
import { CrossSellingModal } from "../CrossSellingModal";
import { chipGreen,chipOrange,chipYellow,chipRed, chipGray, chipblue } from "../../businessLogic/styles";

const euroConfButtons = [
    {id: 17270, label: "fascicolo.fascicoloAdd.amlObligations"},
    {id: 21946, label: "fascicolo.fascicoloAdd.amlRetentions"},
    {id: 21947, label: "fascicolo.fascicoloAdd.amlRetentionObligations"},
]
const signatureStatusMap = {
    SIGNED: { label: "Firmato", sx: chipGreen },
    PENDING: { label: "In attesa di firma", sx: chipYellow },
    REJECTED: { label: "Rifiutato", sx: chipRed },
}

export const NewAllegatiTab = ({
    userPermissions, 
    allBrowserFiles, 
    currentAllegati,
    handleUploadFile,
    handleDownloadAllegatoClick,
    handleDeleteAllegatoClick,
    handleEuroConfClick,
    handleRefreshAllegati
}) => {

    //Constants
    const tenantId = sharedDatasInstance.getPropertyByName("tenantId");
    const aziendaId = sharedDatasInstance.getPropertyByName("aziendaId");
    const hasSignatureCapability = sharedDatasInstance.getPropertyByName("hasSignatureCapability");
    const hasArchiveLicense = sharedDatasInstance.getPropertyByName("hasArchiveLicense");
    const isSignatureEnabled = hasSignatureCapability;
    const isArchiveEnabled = hasArchiveLicense;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar()    
    const { permissions: currentPermissions, getPermissions } = useAreaPermissions(bsaFascicolo);
    const canUpdateAllegati = userPermissions.canUpdate && currentPermissions.canUpdate;
    //States
    const [isLoading, setIsLoading] = useState(false);
    const [signerList, setSignerList] = useState([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [signerModalOpen, setSignerModalOpen] = useState(false);
    const [selectedAllegato, setSelectedAllegato] = useState(null);
    const [isRefreshingAllegati, setIsRefreshingAllegati] = useState(false);
    const [crossSellingModalOpen, setCrossSellingModalOpen] = useState(false);
    const [cctModalOpen, setCctModalOpen] = useState(false);
    const [selectedAllegatoCCT, setSelectedAllegatoCCT] = useState(null);
    const [viewMode, setViewMode] = useState("fascicolo");
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        id: false,
        signedOn: false,
        entityType: false,
        // Punto 7 UI: nascondi le colonne di stato quando la licenza non è attiva,
        // così non ripetiamo un chip "Non abilitato" identico su ogni riga.
        signatureStatus: isSignatureEnabled,
        cctId: isArchiveEnabled,
    });
    const CustomToolbar = (props) => (
        <Stack direction="column" spacing={1} sx={{ p: 1 }}>
            {/* Riga superiore: selettore del tipo di allegati (+ Aggiorna a destra). */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    size="small"
                    onChange={(e, value) => { if (value !== null) setViewMode(value); }}
                >
                    <ToggleButton value="fascicolo">
                        {`Allegati del fascicolo (${allegatiFascicolo.length})`}
                    </ToggleButton>
                    <ToggleButton value="altri">
                        {`Altri allegati (${altriAllegati.length})`}
                    </ToggleButton>
                </ToggleButtonGroup>
                {isSignatureEnabled || isArchiveEnabled && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefreshClick}
                        disabled={isRefreshingAllegati}
                    >
                        {isRefreshingAllegati ? "Aggiornamento..." : "Aggiorna"}
                    </Button>
                )}
            </Stack>
            {/* Riga inferiore: solo Filtri + Colonne invece dell'intera GridToolbar (punto 8 UI). */}
            <GridToolbarContainer sx={{ p: 0 }}>
                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
            </GridToolbarContainer>
        </Stack>
    );
    // Punto 4 UX: messaggio contestuale quando la vista corrente è vuota,
    // al posto del generico "Nessuna riga" del DataGrid.
    const NoRowsOverlay = () => (
        <Stack height="100%" alignItems="center" justifyContent="center" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
                {viewMode === "fascicolo"
                    ? "Nessun allegato nel fascicolo. Carica un file PDF qui sopra per iniziare."
                    : "Nessun altro allegato trovato."}
            </Typography>
        </Stack>
    );
    const columns = useMemo(() => [
        { field: "signedOn", headerName: "Data firma", flex: 0.7, minWidth: 120, hidden: true },
        {
            field: 'displayedName',
            headerName: 'Nome File',
            flex: 1.5,
            minWidth: 220,
        },
        {
            field: 'entityType',
            headerName: 'Riferimento',
            flex: 0.6,
            minWidth: 110,
        },
        {
            field: 'createdOn',
            headerName: 'Data Caricamento',
            flex: 0.7,
            minWidth: 130,
            valueFormatter: (params) => {
                if (params.value == null) {
                    return "";
                }
                return new Date(params.value).toLocaleDateString();
            },
        },
        {
            field: 'signatureStatus',
            headerName: 'Firma Digitale',
            flex: 0.9,
            minWidth: 150,
            renderCell: (params) => isSignatureEnabled ? rendererSignatureStatus(params) : <VaporTag variant='standard' label="Non abilitato" sx={chipblue} />
        },
        {
            field: 'cctId',
            headerName: 'Conservazione',
            flex: 0.9,
            minWidth: 150,
            renderCell: (params) => isArchiveEnabled ? renderCCTStatus(params) : <VaporTag variant='standard' label="Non abilitato" sx={chipblue} />
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.8,
            minWidth: 170,
            filterable: false,
            sortable: false,
            renderCell: (params) => renderActions(userPermissions, params.row)
        }
    ], [userPermissions,isSignatureEnabled,isArchiveEnabled,canUpdateAllegati]);
    //Derived data
    const { allegatiFascicolo, altriAllegati } = useMemo(() => {
        const list = currentAllegati ?? [];
        return {
            allegatiFascicolo: list.filter((a) => a.entityType === "fascicolo"),
            altriAllegati: list.filter((a) => a.entityType !== "fascicolo"),
        };
    }, [currentAllegati]);
    const displayedAllegati = viewMode === "fascicolo" ? allegatiFascicolo : altriAllegati;
    //Api
    const logApi = LogApi();
    const signatureApi = SignatureApi();    

    //Useeffects
    useEffect(() => {
        loadSingatureData();
    },[])
    useEffect(() => {
        // La colonna "Riferimento" è utile solo negli "Altri allegati";
        // nella vista fascicolo è ridondante (tutte le righe sono "fascicolo").
        setColumnVisibilityModel((prev) => ({ ...prev, entityType: viewMode !== "fascicolo" }));
    }, [viewMode])
    //Loads Methods
    async function loadSingatureData(){
        if(isDataLoaded || !isSignatureEnabled) return;
        try {
            setIsLoading(true);
            const signerListResponse = await signatureApi.GetSignerList(tenantId, aziendaId);
            if(signerListResponse.length > 0){
                setSignerList(signerListResponse);
            }
        } catch (error) {
            logApi.AddLog(`Errore nel caricamento dei dati dei firmatari per l'azienda ${aziendaId}: ${error.message}`,error.stack,"loadSingatureData","NewAllegatiTab");
        }
        finally{
            setIsLoading(false);
            setIsDataLoaded(true);
        }
    }
    //Render Methods    
    const rendererSignatureStatus = (params) => {
        const config = signatureStatusMap[params.value] ?? { label: "Non firmato", sx: chipGray };
        return <VaporTag variant='standard' label={config.label} sx={config.sx} />;           
    }
    const renderCCTStatus = (params) => {
        if(params.value){
            return <VaporTag variant='standard' label="Conservato" sx={chipGreen} />;
        }
        else {
            if(params.row.autoSendCCT){
                return <VaporTag variant='standard' label="In elaborazione" sx={chipYellow} />;
            }
            return <VaporTag variant='standard' label="Non conservato" sx={chipGray} />;
        }
    }
    const renderActions = (userPermissions, row) => {
        const isInConservazione = row.cctId !== null;
        const isFirmato = row.signedOn !== null;
        // Punto 2 UX: quando un'azione è disabilitata spieghiamo il perché nel tooltip.
        const deleteDisabled = (isSignatureEnabled || isArchiveEnabled) && isInConservazione;
        const signDisabled = isSignatureEnabled && isFirmato;
        const archiveDisabled = isArchiveEnabled && isInConservazione;
        return (
            <Stack direction='row' spacing={0}>
                {userPermissions.canDelete ? (
                    <IconButton
                        color='error'
                        onClick={(e) => { e.stopPropagation(); handleDeleteAllegatoClick(row); }}
                        disabled={deleteDisabled}
                        title={deleteDisabled ? "Non eliminabile: allegato in conservazione" : "Elimina allegato"}
                    >
                        <DeleteIcon />
                    </IconButton>
                ) : null}
                {isSignatureEnabled && canUpdateAllegati && (
                    <IconButton
                        color='primary'
                        onClick={(e) => { e.stopPropagation(); handleOpenSignatureModal(row); }}
                        disabled={signDisabled}
                        title={signDisabled ? "Documento già firmato" : "Invia alla firma digitale"}
                    >
                        <SignatureIcon />
                    </IconButton>
                )}
                {isArchiveEnabled && canUpdateAllegati && (
                    <IconButton
                        color='primary'
                        onClick={(e) => { e.stopPropagation(); handleOpenCCTModal(row); }}
                        disabled={archiveDisabled}
                        title={archiveDisabled ? "Allegato già in conservazione" : "Invia in conservazione"}
                    >
                        <ArchiveIcon />
                    </IconButton>
                )}
                {userPermissions.canRead ? (
                    <IconButton
                        color='primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            if(!row.isDisabled){
                                handleDownloadAllegatoClick(row)
                            }
                        }}
                        title="Visualizza allegato"
                    >
                        <DetailIcon />
                    </IconButton>
                ) : null}
            </Stack>
        )
    }
    //Handlers Methods
    const handleSign = async (signatureSelection) => {        
        if (!isSignatureEnabled || !userPermissions.canUpdate || !getPermissions().canUpdate) return;
        if(selectedAllegato?.id && signatureSelection.signer && signatureSelection.certificate){
            try {
                setIsLoading(true);
                const signatureOptions = {
                    autoSendCCT: signatureSelection.autoSendCCT,
                    useSignatureTimestamp: signatureSelection.useSignatureTimestamp,
                    metadata: signatureSelection.metadata,
                };
                let signSessionData = await signatureApi.StartSignSession(
                    selectedAllegato.id,
                    aziendaId,
                    signatureSelection.signer,
                    signatureSelection.certificate,
                    signatureSelection.signature,
                    signatureOptions
                )
                if(signSessionData && signSessionData.hubId){
                    enqueueSnackbar("Sessione di firma avviata con successo.", { variant: "success" });
                    logApi.AddLog(`Sessione di firma avviata con successo per l'allegato ${selectedAllegato.id}`, null, "handleSign", "NewAllegatiTab");
                }
            } catch (error) {
                enqueueSnackbar("Errore durante l'avvio della sessione di firma. Riprova.", { variant: "error" });
                logApi.AddLog(`Errore durante l'avvio della sessione di firma: ${error.message}`, error.stack, "handleSign", "NewAllegatiTab");
            } finally {
                setIsLoading(false);
                setSignerModalOpen(false);  
                handleRefreshClick();      
            }
        }
    }
    const handleOpenSignatureModal = (row) => {
        if (!userPermissions.canUpdate || !getPermissions().canUpdate) return;
        if (!isSignatureEnabled) {
            setCrossSellingModalOpen(true);
            return;
        }
        setSelectedAllegato(row);
        setSignerModalOpen(true);
    }
    const handleCloseCrossSellingModal = () => {
        setCrossSellingModalOpen(false);
    }
    const handleOpenCCTModal = (row) => {
        if (!userPermissions.canUpdate || !getPermissions().canUpdate) return;
        if (!isArchiveEnabled) {
            setCrossSellingModalOpen(true);
            return;
        }
        setSelectedAllegatoCCT(row);
        setCctModalOpen(true);
    }
    const handleSendCCT = async (cctData) => {
        if (!isArchiveEnabled || !userPermissions.canUpdate || !getPermissions().canUpdate) return;
        try {    
            setIsLoading(true);       
            let response = await signatureApi.PostToArchive({
                allegatoId: selectedAllegatoCCT.id,
                aziendaId: aziendaId,
                annoRiferimento: Number(cctData.metadata.cct.PeriodoRiferimentoAnno) || new Date().getFullYear(),
                tipoDocumentoAntiriciclaggio: cctData.metadata.cct.TipoDocumento,
                oggetto: cctData.metadata.cct.Oggetto,
                paroleChiave: cctData.metadata.cct.ParoleChiave.map(parolaChiave => parolaChiave.label) || [],
                dataRegistrazione: cctData.metadata.cct.DataDocumento || new Date().toLocaleDateString(),
            })
            if(response && response.cctId > 0){
                enqueueSnackbar("Allegato inviato in conservazione con successo.", { variant: "success" });
                logApi.AddLog(`Invio in conservazione richiesto per l'allegato ${selectedAllegatoCCT?.id}`, null, "handleSendCCT", "NewAllegatiTab");
            }
        } catch (error) {
            enqueueSnackbar("Errore durante l'invio in conservazione. Riprova.", { variant: "error" });
            logApi.AddLog(`Errore durante l'invio in conservazione: ${error.message}`, error.stack, "handleSendCCT", "NewAllegatiTab");
        }
        finally{
            setIsLoading(false);
            setCctModalOpen(false);
            handleRefreshClick();
        }
    }
    const handleCloseSignatureModal = () => {
        setSignerModalOpen(false);
    }
    const handleRefreshClick = async () => {
        if (!handleRefreshAllegati || isRefreshingAllegati) return;
        try {
            setIsRefreshingAllegati(true);
            await handleRefreshAllegati();
        } catch (error) {
            logApi.AddLog(`Errore durante il refresh degli allegati per l'azienda ${aziendaId}: ${error.message}`, error.stack, "handleRefreshClick", "NewAllegatiTab");
        } finally {
            setIsRefreshingAllegati(false);
        }
    }
    return (
        <Fragment >
            <Loading open={isLoading} />
            <ModalSignature
                show={signerModalOpen && canUpdateAllegati}
                onOk={handleSign}
                onClose={handleCloseSignatureModal}
                signerList={signerList}
                currentAllegato={selectedAllegato}                
            />
            <CrossSellingModal
                show={crossSellingModalOpen}
                onClose={handleCloseCrossSellingModal}
            />
            <ModalConservazione
                show={cctModalOpen && canUpdateAllegati}
                onOk={handleSendCCT}
                onClose={() => setCctModalOpen(false)}
                currentAllegato={selectedAllegatoCCT}
            />
            {viewMode === "fascicolo" && (
                <Fragment>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 1, mb: 1 }}>
                        {euroConfButtons.map(({id,label}) => (
                            <Button
                                key={id}
                                variant="text"
                                size="small"
                                startIcon={<img src="/img/EcLogo.png" width={20} alt="EcLogo" />}
                                sx={{ width: "fit-content !important" }}
                                onClick={() => handleEuroConfClick(id)}
                            >
                                {t(label)}
                            </Button>))
                        }
                    </Stack>
                    {userPermissions.canCreate ? (
                        <VaporUploader
                            buttonVariant="contained"
                            draggable
                            multiple
                            label=""
                            draggableText="Trascina qui i tuoi files PDF (MAX 20 files, 10MB ciascuno)"
                            dropText="Rilascia qui i tuoi files PDF (MAX 20 files, 10MB ciascuno)"
                            buttonLabel="Seleziona allegati"
                            onFileUpload={handleUploadFile()}
                            files={allBrowserFiles}
                            inputFileProps={{
                                multiple: true,
                            }}
                            xs={{ mt: 2, mb:2 }}
                        />
                    ) : <NotAuth />}
                </Fragment>
            )}
            {userPermissions.canRead && (
                <DataGridPro
                    rows={displayedAllegati}
                    columns={columns}
                    sx={{ mt: 2 }}
                    density="standard"
                    slots={{ toolbar: CustomToolbar, noRowsOverlay: NoRowsOverlay }}
                    localeText={
                        itIT.components.MuiDataGrid.defaultProps.localeText
                    }
                    pageSizeOptions={[5,10]}
                    autoHeight={true}
                    onRowClick={(params) => handleDownloadAllegatoClick(params.row)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                    }}
                    slotProps={{
                        toolbar: { printOptions: { disableToolbarButton: true } },
                    }} 
                />
            )}
        </Fragment>
    )
}